/**
 * The shape every offline-recording error code has, pinned Rust-side by the
 * exhaustive code test on the error enum and by the delete-replay module's own
 * prefix test: `OFFLINE_` followed by SCREAMING_SNAKE.
 *
 * Bounded deliberately. An unbounded tail would let a long code become an
 * unbounded analytics property.
 */
const OFFLINE_ERROR_CODE = /^OFFLINE_[A-Z0-9]+(_[A-Z0-9]+)*$/;

/** The longest code Rust declares is well under this; the cap is the backstop. */
const MAX_CODE_LENGTH = 64;

/**
 * Narrow an error code arriving from Rust to something safe to put in an
 * analytics property, or `null`.
 *
 * Analytics props on this path reach Datadog, so the only values allowed
 * through are ones matching the documented `OFFLINE_*` contract. Rust today
 * emits nothing else — but the value crosses an IPC boundary whose rejection
 * shape this side does not control, and a future code carrying a message would
 * otherwise ship verbatim. Anything unrecognised becomes `null` rather than a
 * placeholder: `null` already means "the rejection named no code", and a
 * malformed code is the same fact from the reader's point of view.
 */
export const toTelemetryErrorCode = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  if (value.length > MAX_CODE_LENGTH) return null;
  return OFFLINE_ERROR_CODE.test(value) ? value : null;
};

/**
 * There is deliberately no invoke-rejection variant of this helper.
 *
 * A rejected `invoke` of an offline-recording command does NOT carry a code.
 * `impl From<OfflineRecordingError> for tauri::ipc::InvokeError` converts with
 * `.to_string()` — Display, not `Serialize`, because a `Serialize` impl on the
 * enum would collide with Tauri's blanket conversion — so the value reaching
 * JS is a sanitized human-readable string such as
 * `No cached identity — online login required`. None of the Display strings
 * contains its `OFFLINE_*` code, so there is nothing to parse out either.
 *
 * `HasErrorCode` is real, but it rides on EVENT payloads, which is why
 * {@link toTelemetryErrorCode} above is fed from a settled event and works.
 * A helper that reads `error_code` off an invoke rejection would return `null`
 * on every real failure while looking as though it worked — and would only
 * ever pass a test that mocked a shape the command cannot produce.
 *
 * Giving the invoke boundary a machine-readable code is a change to the shared
 * Rust error contract — every offline-recording command goes through that one
 * `From` impl, so it is all of them or none, not this one call.
 */
